import React from "react";
import {
  Page,
  View,
  Text,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";

// Font registration
Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "https://cdn.jsdelivr.net/npm/@fontsource/roboto/files/roboto-latin-400-normal.woff",
      fontWeight: "normal",
    },
    {
      src: "https://cdn.jsdelivr.net/npm/@fontsource/roboto/files/roboto-latin-700-normal.woff",
      fontWeight: "bold",
    },
  ],
});

Font.registerHyphenationCallback((word) => [word]);

const maxValue = 100;

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: "Roboto",
    position: "relative",
    paddingTop: 110,
    paddingBottom: 85,
  },
  logo2: {
    position: "absolute",
    top: 30,
    left: 30,
    width: 60,
  },
  upperDesign: {
    position: "absolute",
    top: -10,
    right: 0,
    width: 280,
  },
  lowerDesign: {
    position: "absolute",
    bottom: -10,
    right: 0,
    width: 300,
  },
  heading: {
    fontSize: 23,
    fontWeight: "bold",
    paddingBottom: 6,
  },
  subHeading: {
    fontSize: 23,
    fontWeight: "bold",
    marginBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: "#000",
    paddingBottom: 15,
  },
  subheading: {
    fontSize: 14,
    marginBottom: 10,
  },
  title: {
    fontSize: 13,
    textAlign: "center",
    marginBottom: 18,
  },
  // Horizontal chart styles
  chartArea: {
    marginTop: 5,
    paddingHorizontal: 10,
  },
  careerRow: {
    marginBottom: 16,
  },
  careerLabel: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 5,
  },
  barRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 3,
  },
  barLabel: {
    fontSize: 8,
    width: 50,
    color: "#555",
  },
  barTrack: {
    flex: 1,
    height: 14,
    backgroundColor: "#f0f0f0",
    borderRadius: 3,
    position: "relative",
    overflow: "hidden",
  },
  barFill: {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    borderRadius: 3,
  },
  barValue: {
    fontSize: 8,
    width: 28,
    textAlign: "right",
    fontWeight: "bold",
  },
  legendContainer: {
    flexDirection: "row",
    justifyContent: "center",
    marginTop: 16,
    gap: 24,
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  legendColor: {
    width: 10,
    height: 10,
    marginRight: 5,
    borderRadius: 2,
  },
  legendText: {
    fontSize: 10,
  },
  pageNumber_Box: {
    position: "absolute",
    bottom: 20,
    left: 10,
    width: 25,
    height: 25,
    border: "2px solid orange",
    borderRadius: 15,
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    zIndex: 2,
  },
  pageNumber: {
    fontSize: 11,
    color: "#000",
    textAlign: "center",
  },
});

const MarketDemandPagePDF = ({ market_demand, assets, name, showPageNumber }) => {
  return (
    <Page size="A4" style={styles.page}>
      <Image src={assets.upper} style={styles.upperDesign} fixed />
      <Image src={assets.lower} style={styles.lowerDesign} fixed />
      <Image src={assets.logo2} style={styles.logo2} fixed />

      <Text style={styles.heading}>Shaping the Future:</Text>
      <Text style={styles.subHeading}>Career Trends You Should Know</Text>
      <Text style={styles.subheading}>
        Below is a visualization of how these careers are expected to grow
        over time:
      </Text>

      <Text style={styles.title}>
        Career Pathways: Current vs Future Market Trends ({name})
      </Text>

      {/* Horizontal bar chart */}
      <View style={styles.chartArea}>
        {market_demand.map((item, i) => {
          const career = item.career_path || item.career_pathway || `Career ${i + 1}`;
          const current = Number(item.current_demand) || 0;
          const future = Number(item.future_demand) || 0;

          return (
            <View key={i} style={styles.careerRow} wrap={false}>
              <Text style={styles.careerLabel}>
                {i + 1}. {career}
              </Text>

              {/* Current demand bar */}
              <View style={styles.barRow}>
                <Text style={styles.barLabel}>Current</Text>
                <View style={styles.barTrack}>
                  <View
                    style={[
                      styles.barFill,
                      {
                        backgroundColor: "#FDB813",
                        width: `${(current / maxValue) * 100}%`,
                      },
                    ]}
                  />
                </View>
                <Text style={styles.barValue}>{current}%</Text>
              </View>

              {/* Future demand bar */}
              <View style={styles.barRow}>
                <Text style={styles.barLabel}>Future</Text>
                <View style={styles.barTrack}>
                  <View
                    style={[
                      styles.barFill,
                      {
                        backgroundColor: "#F57C00",
                        width: `${(future / maxValue) * 100}%`,
                      },
                    ]}
                  />
                </View>
                <Text style={styles.barValue}>{future}%</Text>
              </View>
            </View>
          );
        })}
      </View>

      {/* Legend */}
      <View style={styles.legendContainer}>
        <View style={styles.legendItem}>
          <View style={[styles.legendColor, { backgroundColor: "#FDB813" }]} />
          <Text style={styles.legendText}>Current Demand</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendColor, { backgroundColor: "#F57C00" }]} />
          <Text style={styles.legendText}>Future Demand</Text>
        </View>
      </View>

      <>
        {showPageNumber && (
          <View style={styles.pageNumber_Box} fixed>
            <Text
              style={styles.pageNumber}
              render={({ pageNumber, totalPages }) => `${pageNumber}`}
              fixed
            />
          </View>
        )}
      </>
    </Page>
  );
};

export default MarketDemandPagePDF;
